/**
 * OrgEvalEmbedPage — モバイルのホーム(社長)に埋め込む「組織の評価」カード。
 *
 * モバイルはこれを `/embed/home/org-eval`（EmbeddedShell 配下）として WebView で表示する。
 * → 以後のレーダー/点数変更は Web デプロイのみで反映（審査不要）。
 *
 * 【v3へ統一（2026-08-22）】
 *  以前は旧路線（buildOrgReportFromResults / 12原型）で算出していたため、
 *  同じ会社でもホームの点数とGB組織レポートの点数が食い違っていた。
 *  取得と算出を組織レポート v3 と同じ useOrgReportV3Data に寄せ、
 *  ホームは「レポートの先頭部分だけを抜き出したもの」にする。
 *  数値・型・キャッチはすべてレポートと同じ値になる。
 */
import React from 'react';
import { useOrgReportV3Data } from '../hooks/useOrgReportV3Data';
import { OrgDiamondV3 } from './OrgDiamondV3';
import { GRADE_LABEL, SCORE_LABEL, type Grade, type ScoreKey } from '../utils/orgScoreV3';

const GRADE_COLOR: Record<Grade, string> = {
  A: '#2E7D32',
  B: '#1565C0',
  C: '#B8860B',
  D: '#B33A3A',
};

const SCORE_KEYS: ScoreKey[] = ['wellbeing', 'connection', 'retention'];

const wrap: React.CSSProperties = {
  fontFamily: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif',
  padding: 16,
  background: '#fff',
  color: '#1a1a1a',
};

export const OrgEvalEmbedPage: React.FC = () => {
  // つながりの構造はこのカードでは出さないので取りに行かない（表示は同じ）
  const { data, loading, error } = useOrgReportV3Data({ skipConsult: true });

  if (loading) {
    return (
      <div style={{ ...wrap, textAlign: 'center', color: '#888', fontSize: 13 }}>読み込み中…</div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ ...wrap, color: '#666', fontSize: 13, lineHeight: 1.6 }}>
        {error ?? '組織の評価を取得できませんでした。'}
      </div>
    );
  }

  const { scores, axisMeans, type, texts } = data;
  const gradeColor = GRADE_COLOR[scores.grade];

  return (
    <div style={wrap}>
      {/* レーダー(組織ダイヤモンド)＋GB組織スコアを横並び。レポートと同じ部品・同じ数値。 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ flexShrink: 0, width: 150 }}>
          <OrgDiamondV3 axisMeans={axisMeans} maxWidth={150} />
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 96 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>GB組織スコア</div>
          <div
            style={{
              fontSize: 40,
              fontWeight: 700,
              color: gradeColor,
              lineHeight: 1,
              marginTop: 2,
            }}
          >
            {scores.gbScore}
            <span style={{ fontSize: 15, color: '#888' }}>/100</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: gradeColor, marginTop: 6 }}>
            {scores.grade} — {GRADE_LABEL[scores.grade]}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            GB組織タイプ {type.code}
            {type.shape ? `／${type.shape}` : ''}
          </div>
        </div>
      </div>

      {/* 3スコアの内訳。レポートの「この評価が示していること」と同じ値。
          ホーム側のWebViewは高さ320px固定（変更にはアプリ更新が要る）ため1行に収める。 */}
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          fontSize: 11,
          color: '#6b7280',
        }}
      >
        {SCORE_KEYS.map((k) => (
          <span key={k}>
            {SCORE_LABEL[k]}{' '}
            <b style={{ color: GRADE_COLOR[scores[k].grade], fontSize: 13 }}>{scores[k].score}</b>
          </span>
        ))}
      </div>

      {texts.catch?.見出し && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#333', lineHeight: 1.5 }}>
          {texts.catch.見出し}
        </div>
      )}
    </div>
  );
};

export default OrgEvalEmbedPage;
